/**
 * O acompanhamento da varredura — depois do mergulho, ainda no abismo.
 *
 * O layout segue o template original: nada de cartão. Um título estático com
 * gradiente, o termo em mono abaixo dele, uma linha fina de progresso, o
 * nome da fase atual com a contagem, e o detalhe dela — tudo flutuando direto
 * sobre a água, sem caixa nenhuma por trás. O que NÃO segue o original é a
 * paleta: lá a câmera emergia para uma superfície clara; aqui ela só recua —
 * a água continua escura, com o texto e as ações de carregamento em branco
 * por cima, para que o mergulho não termine num corte de tela clara no meio
 * do movimento.
 *
 * O que muda em relação ao HTML de origem: o progresso agora é REAL — o
 * pipeline emite por SSE, não uma narração cronometrada que corria por conta
 * própria e podia terminar antes da rodada de verdade. E os artefatos que a
 * rodada já trouxe não aparecem mais nesta página: viraram um contador ao
 * vivo na barra de navegação (Moldura.tsx), visível em qualquer tela, porque
 * o pipeline grava a cada licitação, não no fim.
 */
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { acompanhar, api, type LicitacaoResumo, type Progresso } from "../api/cliente";
import { useSessao } from "../sessao";
import { useAgua } from "../componentes/Agua";
import { dia } from "../formato";
import css from "./Varredura.module.css";

export function Varredura() {
  const { id = "" } = useParams();
  const [p, setP] = useState<Progresso | null>(null);
  const { emergir, repousar, fossa } = useAgua();
  const cliente = useQueryClient();
  const { podeOperar } = useSessao();
  const [erroParar, setErroParar] = useState<string | null>(null);

  // A câmera recua ao entrar — o inverso exato do avanço do mergulho —, mas
  // a água continua no abismo escuro: é o que liga as duas telas como um
  // movimento contínuo em vez de um corte para uma tela clara.
  //
  // `fossa` libera a luz do shader: sem ela, o filtro CSS que puxa a tela
  // para preto deixava o brilho sobreviver em UM só ponto — a massa clara
  // do ruído cai sempre do mesmo lado. Com ela, a luminescência cobre o
  // quadro e o fluxo atravessa a tela em vez de morar num canto.
  useEffect(() => {
    emergir();
    fossa(true);
    return () => {
      fossa(false);
      repousar();
    };
  }, [emergir, repousar, fossa]);

  const terminou =
    p?.estado === "concluida" ||
    p?.estado === "falhou" ||
    p?.estado === "cancelada";

  const navegar = useNavigate();
  const [semColeta, setSemColeta] = useState(false);

  // A PRIMEIRA LEITURA NAO PODE SER A UNICA. Antes ela rodava uma vez e, se
  // falhasse, `p` ficava nulo para sempre: a tela dizia "Consultando" e nunca
  // mais perguntava nada — a busca "nunca saia desse estado" (gestao,
  // 2026-09-23). O servidor devolve o id "0" quando o coletor leva mais de
  // 10 s para abrir a coleta (maquina fria, varredura na fila); "0" nao existe
  // (404), e o comentario da rota promete que a tela acha a coleta sozinha.
  useEffect(() => {
    if (id === "0") return;
    void api.estadoRodada(id).then(setP).catch(() => {});
  }, [id]);

  // Rede de seguranca: se o canal de eventos cair (proxy, rede, reinicio da
  // API), o estado continua chegando por consulta simples.
  useEffect(() => {
    if (id === "0" || terminou) return;
    const t = setInterval(() => {
      void api.estadoRodada(id).then(setP).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [id, terminou]);

  // Id "0": procura a coleta que o coletor acabou de abrir e vai para ela.
  useEffect(() => {
    if (id !== "0") return;
    let vivo = true;
    const inicio = Date.now();
    const t = setInterval(() => {
      void api
        .rodadaAtual()
        .then((a) => {
          if (vivo && a && a.rodada_id !== "0") {
            navegar(`/varredura/${a.rodada_id}`, { replace: true });
          } else if (vivo && Date.now() - inicio > 45000) {
            setSemColeta(true);
          }
        })
        .catch(() => {});
    }, 2000);
    return () => {
      vivo = false;
      clearInterval(t);
    };
  }, [id, navegar]);

  useEffect(() => {
    if (!p || terminou) return;
    return acompanhar(id, setP, (fim) => {
      if (fim) setP(fim);
      void cliente.invalidateQueries();
    });
  }, [id, p?.rodada_id, terminou, cliente]);

  const pct = Math.round((p?.fracao ?? 0) * 100);
  const comErro = p?.estado === "falhou";
  // Cancelada nao e erro: a barra nao fica vermelha, e o texto diz que alguem
  // mandou parar em vez de sugerir que algo quebrou.
  const cancelada = p?.estado === "cancelada";
  const rodando = !!p && !terminou;

  async function parar() {
    setErroParar(null);
    try {
      setP(await api.pararRodada(id));
    } catch (x) {
      // O 409 chega aqui quando a coleta terminou sozinha entre o clique e a
      // requisicao. Mostrar a mensagem do servidor e melhor que engolir: o
      // botao sumindo sem explicacao pareceria clique perdido.
      setErroParar(x instanceof Error ? x.message : "não deu para parar");
    }
  }

  return (
    <div className={css.tela}>
      <div className={css.centro} aria-live="polite" aria-busy={!terminou}>
        <h1 className={css.titulo}>Navegando pelas melhores oportunidades!</h1>
        <span className={css.alvo}>{p?.termo ?? ""}</span>

        <div
          className={css.barra}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <i style={{ width: `${pct}%` }} data-falhou={comErro} />
        </div>

        <p className={css.etapa}>
          <b>
            {p?.estado === "na_fila"
              ? "Na fila"
              : (p?.titulo ?? "Iniciando a busca")}
          </b>
          {p && p.total > 0 && (
            <span>
              {p.feitos}/{p.total}
            </span>
          )}
        </p>
        <p className={comErro ? css.detalheErro : css.detalheEtapa}>
          {p?.estado === "na_fila"
            ? `Posição ${(p.posicao_na_fila ?? 0) + 1} — uma varredura por vez, a conta do Licit Mais é uma só.`
            : semColeta
              ? "A coleta não abriu. Volte e tente de novo — se repetir, avise o suporte."
              : (p?.erro ?? p?.detalhe ?? "…")}
        </p>

        {cancelada && (
          <p className={css.detalheEtapa}>Varredura parada pelo operador.</p>
        )}
        {erroParar && <p className={css.detalheErro}>{erroParar}</p>}

        {/* Enquanto corre, a saida e parar; depois, seguir para o resultado.
            Os dois nunca aparecem juntos — sao o mesmo lugar da tela em dois
            momentos, nao duas opcoes simultaneas. */}
        {rodando && podeOperar && (
          <button
            type="button"
            className={css.parar}
            onClick={() => void parar()}
            disabled={p.cancelando}
          >
            {p.cancelando ? "Parando…" : "Parar varredura"}
          </button>
        )}
        {rodando && p.cancelando && (
          <p className={css.detalheEtapa}>
            Parada pedida — o coletor encerra no próximo checkpoint de página.
          </p>
        )}

        {terminou && (
          <Link to="/oportunidades" className={css.cta}>
            Ver oportunidades
          </Link>
        )}
      </div>

      <Encontradas rodada={id} rodando={rodando} />
    </div>
  );
}

/**
 * AS ENCONTRADAS NESTA VARREDURA, as mais recentes primeiro — pedido da
 * gestao, 2026-09-16: o contador na navegacao dizia "2" e nao havia onde ver
 * quais eram.
 *
 * Mora AQUI, e nao em Oportunidades, porque recem-encontrada ainda nao passou
 * pela triagem nem pelo score: nao tem faixa, e nenhuma aba de Oportunidades a
 * mostraria ate a rodada da madrugada pontuar. Esta lista e o unico lugar em
 * que ela existe antes disso.
 */
function Encontradas({ rodada, rodando }: { rodada: string; rodando: boolean }) {
  const { data } = useQuery({
    queryKey: ["achados-lista", rodada],
    queryFn: () => api.achados(rodada),
    enabled: !!rodada,
    // Continua perguntando DEPOIS que a varredura termina: o acompanhante
    // segue triando e lendo editais, e a etapa de cada uma muda nessa hora.
    refetchInterval: 10_000,
  });
  if (!data || data.total === 0) {
    return rodando ? (
      <section className={css.encontradas}>
        <h2>Encontradas nesta varredura</h2>
        <p className={css.encontradasVazio}>
          Nenhuma ainda. Elas aparecem aqui assim que são encontradas.
        </p>
      </section>
    ) : null;
  }
  // Cada parcela com o seu nome, contada no servidor. A tela fazia
  // `total - itens.length` e atribuía a diferença inteira a "prazo encerrado
  // ou termo negativo" — e, desde a Regra 1 no servidor, a maior parte era
  // score abaixo de 50.
  const motivos = [
    data.fora_do_funil
      ? `${data.fora_do_funil} ficaram fora dos filtros do funil (prazo encerrado ou situação suspensa, revogada ou anulada)`
      : "",
    data.ocultas_regra_1
      ? `${data.ocultas_regra_1} tiraram score abaixo de 50 e ficam fora da tela pela Regra 1 do documento de regras`
      : "",
    // A reprovada nao volta a aparecer (pedido da gestao, 2026-09-28): a
    // varredura relista tudo o que continua aberto, inclusive o que alguem
    // ja reprovou. Ela fica so na aba Reprovadas.
    data.reprovadas
      ? `${data.reprovadas} já foram reprovadas por alguém da equipe e ficam só na aba Reprovadas`
      : "",
  ].filter(Boolean);
  return (
    <section className={css.encontradas}>
      <h2>
        Encontradas nesta varredura <span>{data.total}</span>
      </h2>
      <p className={css.encontradasNota}>
        As mais recentes primeiro, atualizado a cada 10 segundos. Cada uma
        passa por triagem, score e pré-análise enquanto a varredura corre — uma
        pré-análise por vez, então as últimas da lista esperam a vez delas.
        {motivos.length ? ` Não aparecem aqui: ${motivos.join("; ")}.` : ""}
      </p>
      <ul className={css.encontradasLista}>
        {data.itens.map((i) => (
          <li key={i.id}>
            <Link to={`/licitacao/${i.id}`}>
              <b>{i.orgao ?? "Órgão não informado"}</b>
              <span className={css.encontradaMeta}>
                {[i.municipio, i.uf].filter(Boolean).join(" / ")}
                {i.encerramento ? ` · encerra ${dia(i.encerramento)}` : ""}
                {i.valor_estimado ? ` · ${i.valor_estimado}` : ""}
                {/* A varredura relista tudo o que continua aberto: sem a data,
                    uma licitação vista há dez dias parecia nova. */}
                {i.vista_em ? ` · no Nectuno desde ${dia(i.vista_em)}` : ""}
                {i.decisao === "aprovada" ? " · já aprovada" : ""}
              </span>
              <span className={css.encontradaObjeto}>{i.objeto}</span>
              <Etapa i={i} />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Em que ponto do funil a encontrada esta, lido do que JA esta gravado — e
 * nao de um estado do acompanhante, que nao tem onde guardar um. Triada sem
 * score nao existe (a triagem pontua na hora), entao `score` nulo e "ainda na
 * triagem".
 */
function Etapa({ i }: { i: LicitacaoResumo }) {
  if (i.score == null) {
    return <span className={css.etapaFila}>Aguardando triagem e score</span>;
  }
  const score = `Score ${i.score}`;
  if (i.analise_recomendacao) {
    return (
      <span className={css.etapaPronta}>
        {score} · pré-análise {i.analise_recomendacao}
      </span>
    );
  }
  if (i.edital === "ausente" || i.edital === "indisponivel") {
    return <span className={css.etapaFeita}>{score} · sem edital para ler</span>;
  }
  if (i.edital === "nao_perguntado") {
    return <span className={css.etapaFeita}>{score} · buscando o edital</span>;
  }
  return <span className={css.etapaFeita}>{score} · pré-análise na fila</span>;
}
